import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faPencilAlt, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getChecklistItems, saveChecklistItems, deleteChecklistItem } from '../services/api';
import ConfirmationModal from './ConfirmationModal';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const SidePanelContainer = styled.div`
  width: 30%;
  height: calc(100vh - 100px);
  background-color: white;
  padding: 0;
  overflow-y: auto;
  position: fixed;
  left: ${props => props.isBudgetOpen ? '32%' : '20px'};
  top: 80px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transform: translateX(${props => props.isVisible ? '0' : '-120%'});
  transition: all 0.3s ease-in-out;
  z-index: 1000;
  border-radius: 12px;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 70vh;
    left: 0;
    top: auto;
    bottom: 0;
    border-radius: 20px 20px 0 0;
    transform: translateY(${props => props.isVisible ? '0' : '100%'});
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const Title = styled.h2`
  color: #316C6E;
  margin: 0 0 20px 0;
  text-align: center;
`;

const ChecklistContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 20px;
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const ItemText = styled.span`
  text-decoration: ${props => props.isCompleted ? 'line-through' : 'none'};
  color: ${props => props.isCompleted ? '#888' : 'inherit'};
  flex: 1;
`;

const ItemInput = styled.input`
  flex: 1;
  padding: 8px;
  border: none;
  background: transparent;
  font-size: 14px;
  border-bottom: 1px solid #316C6E;
  
  &:focus {
    outline: none;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.color || '#666'};
  cursor: pointer;
  padding: 5px;
  margin-left: 5px;
  
  &:hover {
    color: ${props => props.hoverColor || '#333'};
  }
`;

const AddItem = styled(ChecklistItem)`
  color: #666;
  background-color: #f8f8f8;
  border-radius: 6px;
  cursor: pointer;
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const SaveButton = styled.button`
  background-color: #316C6E;
  color: white;
  border: none;
  padding: 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 20px;
  
  &:hover {
    background-color: #254F51;
  }
`;

const ChecklistModal = ({ onClose, itineraryId, isBudgetOpen }) => {
  const { language } = useLanguage();
  const t = translations[language].checklist;
  
  const [isVisible, setIsVisible] = useState(false);
  const [items, setItems] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const fetchedItems = await getChecklistItems(itineraryId);
        if (fetchedItems.length === 0) {
          const savedItems = await Promise.all(
            defaultItems.map(item => 
              saveChecklistItems(itineraryId, [{ 
                description: item.description,
                isCompleted: false 
              }])
            )
          );
          setItems(savedItems.flat());
        } else {
          setItems(fetchedItems);
        }
      } catch (error) {
        console.error('Error fetching checklist items:', error);
      }
    };
    fetchItems();
  }, [itineraryId]);

  const handleAddItem = async () => {
    try {
      const newItem = await saveChecklistItems(itineraryId, [{ description: '', isCompleted: false }]);
      setItems([...items, ...newItem]);
      setEditingIndex(items.length);
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const handleItemClick = async (index) => {
    if (editingIndex !== null) return;
    
    try {
      const updatedItem = { 
        ...items[index], 
        isCompleted: !items[index].isCompleted 
      };
      await saveChecklistItems(itineraryId, [updatedItem]);
      
      const newItems = [...items];
      newItems[index] = updatedItem;
      setItems(newItems);
    } catch (error) {
      console.error('Error updating item completion:', error);
    }
  };

  const handleEditComplete = async () => {
    if (editingIndex === null) return;

    try {
      const updatedItem = {
        ...items[editingIndex],
        description: editValue.trim()
      };
      
      if (updatedItem.description) {
        await saveChecklistItems(itineraryId, [updatedItem]);
        const newItems = [...items];
        newItems[editingIndex] = updatedItem;
        setItems(newItems);
      }
    } catch (error) {
      console.error('Error saving item:', error);
    }
    
    setEditingIndex(null);
    setEditValue('');
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteChecklistItem(itemToDelete.id);
      setItems(items.filter(item => item.id !== itemToDelete.id));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
    setShowConfirmDelete(false);
    setItemToDelete(null);
  };

  useEffect(() => {
    let touchStartY = 0;
    let touchEndY = 0;
    
    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };
    
    const handleTouchMove = (e) => {
      touchEndY = e.touches[0].clientY;
    };
    
    const handleTouchEnd = () => {
      const swipeDistance = touchEndY - touchStartY;
      if (swipeDistance > 100) {
        handleClose();
      }
    };
    
    if (window.innerWidth <= 768) {
      const element = document.getElementById('checklist-panel');
      if (element) {
        element.addEventListener('touchstart', handleTouchStart);
        element.addEventListener('touchmove', handleTouchMove);
        element.addEventListener('touchend', handleTouchEnd);
      }
      
      return () => {
        if (element) {
          element.removeEventListener('touchstart', handleTouchStart);
          element.removeEventListener('touchmove', handleTouchMove);
          element.removeEventListener('touchend', handleTouchEnd);
        }
      };
    }
  }, []);

  const defaultItems = [
    { description: t.defaultItems.passport, isCompleted: false },
    { description: t.defaultItems.sunscreen, isCompleted: false },
    { description: t.defaultItems.beach, isCompleted: false },
    { description: t.defaultItems.shoes, isCompleted: false },
    { description: t.defaultItems.money, isCompleted: false }
  ];

  return (
    <SidePanelContainer 
      isVisible={isVisible} 
      id="checklist-panel"
      isBudgetOpen={isBudgetOpen}
    >
      <CloseButton onClick={handleClose}>
        <FontAwesomeIcon icon={faTimes} />
      </CloseButton>
      
      <Title>{t.title}</Title>
      <ChecklistContainer>
        {items.map((item, index) => (
          <ChecklistItem key={index}>
            {editingIndex === index ? (
              <ItemInput
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleEditComplete}
                onKeyPress={(e) => e.key === 'Enter' && handleEditComplete()}
                autoFocus
              />
            ) : (
              <ItemContent onClick={() => handleItemClick(index)}>
                <IconButton color={item.isCompleted ? '#4CAF50' : '#ddd'}>
                  <FontAwesomeIcon icon={faCheck} />
                </IconButton>
                <ItemText isCompleted={item.isCompleted}>
                  {item.description}
                </ItemText>
              </ItemContent>
            )}
            <div>
              <IconButton 
                onClick={() => {
                  setEditingIndex(index);
                  setEditValue(item.description);
                }}
                color="#666"
                hoverColor="#316C6E"
              >
                <FontAwesomeIcon icon={faPencilAlt} />
              </IconButton>
              <IconButton 
                onClick={() => {
                  setItemToDelete(item);
                  setShowConfirmDelete(true);
                }}
                color="#dc3545"
                hoverColor="#c82333"
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </div>
          </ChecklistItem>
        ))}
        <AddItem onClick={handleAddItem}>
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: '10px' }} />
          {t.addNew}
        </AddItem>
      </ChecklistContainer>

      {showConfirmDelete && (
        <ConfirmationModal
          isOpen={showConfirmDelete}
          onClose={() => setShowConfirmDelete(false)}
          onConfirm={handleConfirmDelete}
          title={t.deleteConfirm.title}
          message={t.deleteConfirm.message}
        />
      )}
    </SidePanelContainer>
  );
};

export default ChecklistModal; 